import { FoodItem, FoodItemTag, FoodTag } from "client/jspPlatformExperiment";
import { FoodItemWithVariants } from "utils/food/foodVariantUtils";

/** One tag link change on a food; a variant names its main as well. */
export interface FoodTagEdit {
  foodId: number;
  mainFoodId: number;
  tag: FoodTag;
  isAdding: boolean;
}

/**
 * What a matrix cell is doing: "sending" while its edit is in the request
 * on the wire, "queued" while it waits for the next batch (and can still
 * be cancelled by clicking it again).
 */
export type TagCellEditState = "sending" | "queued";

/** Stable key for one (food, tag) cell of the tag matrix. */
export const tagCellKey = ({
  foodId,
  tagId,
}: {
  foodId: number;
  tagId: number;
}): string => `${foodId}:${tagId}`;

/**
 * A copy of `food` with `tag` linked (a fresh link built from the tag option)
 * or its link dropped. Mirrors what the batch tag edit endpoint does server
 * side, so the cached food can be patched right after a confirmed single-tag
 * edit without waiting for the incremental refresh.
 */
export const withFoodTagToggled = ({
  food,
  tag,
  isAdding,
}: {
  food: FoodItem;
  tag: FoodTag;
  isAdding: boolean;
}): FoodItem => {
  // Drop any existing link (active or removed) so an add never duplicates
  const remaining = (food.food_tags ?? []).filter(
    (foodTag) => foodTag.id !== tag.id,
  );
  if (!isAdding) {
    return { ...food, food_tags: remaining };
  }
  const linked: FoodItemTag = {
    id: tag.id,
    tag_name: tag.tag_name,
    tag_description: tag.tag_description,
    tag_categories: tag.tag_categories,
  };
  return { ...food, food_tags: [...remaining, linked] };
};

/**
 * A copy of `main` with the nested variant of the same id as `variant`
 * replaced. The tag board reads variant rows from their main's
 * `variant_food_items`, so a variant edit has to be written back through
 * its main for the row to pick it up.
 */
export const withVariantReplaced = ({
  main,
  variant,
}: {
  main: FoodItemWithVariants;
  variant: FoodItem;
}): FoodItemWithVariants => ({
  ...main,
  variant_food_items: (main.variant_food_items ?? []).map((nested) =>
    nested.id === variant.id ? variant : nested,
  ),
});
